// Shared SEO schema constants for JSON-LD structured data

export const ORGANIZATION_JSONLD = {
  '@type': 'Organization',
  '@id': 'https://venueintel.xyz/#organization',
  name: 'VenueIntel',
  url: 'https://venueintel.xyz',
  logo: 'https://venueintel.xyz/favicon.png',
  foundingDate: '2025',
  description:
    'AI-powered decision intelligence for nightclubs and music venues. Connects POS, ticketing, CRM, and marketing into one intelligence layer.',
  founders: [
    {
      '@type': 'Person',
      name: 'Lucas Santos',
      jobTitle: 'Co-Founder & CEO',
      url: 'https://venueintel.xyz/about',
      sameAs: ['https://www.linkedin.com/in/lucassantos90/'],
    },
    {
      '@type': 'Person',
      name: 'Molly Shelestak',
      jobTitle: 'Co-Founder & CPO',
      url: 'https://venueintel.xyz/about',
      sameAs: ['https://www.linkedin.com/in/mollyshelestak/'],
    },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'lucas@venueintel.xyz',
    contactType: 'sales',
  },
  sameAs: ['https://www.linkedin.com/company/venueintel', 'https://x.com/VenueIntel'],
  areaServed: 'US',
};

export const WEBSITE_JSONLD = {
  '@type': 'WebSite',
  '@id': 'https://venueintel.xyz/#website',
  name: 'VenueIntel',
  url: 'https://venueintel.xyz',
  publisher: { '@id': 'https://venueintel.xyz/#organization' },
};

export const WEB_APPLICATION_JSONLD = {
  '@type': 'WebApplication',
  '@id': 'https://venueintel.xyz/#application',
  name: 'VenueIntel',
  url: 'https://venueintel.xyz',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web browser',
  description:
    'Decision intelligence platform for nightclubs and music venues. Connects POS, ticketing, CRM, and marketing into one intelligence layer that learns your venue.',
  offers: [
    {
      '@type': 'Offer',
      name: 'Core',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '349',
        priceCurrency: 'USD',
        unitText: 'MONTH',
      },
    },
    {
      '@type': 'Offer',
      name: 'Pro',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '599',
        priceCurrency: 'USD',
        unitText: 'MONTH',
      },
    },
    {
      '@type': 'Offer',
      name: 'Operator',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '899',
        priceCurrency: 'USD',
        unitText: 'MONTH',
      },
    },
    {
      '@type': 'Offer',
      name: 'Enterprise',
      availability: 'https://schema.org/InStoreOnly',
      description: 'Custom pricing for multi-venue operations',
    },
  ],
  creator: { '@id': 'https://venueintel.xyz/#organization' },
};

export const FAQ_ITEMS = [
  {
    question: 'What is VenueIntel?',
    answer:
      'VenueIntel is a decision intelligence platform for nightclubs and music venues. It connects your POS, ticketing, CRM, and marketing tools into one layer — giving you real-time dashboards, automated reports, and AI-powered recommendations.',
  },
  {
    question: 'How does VenueIntel connect to my existing tools?',
    answer:
      'We integrate directly with tools like Toast, Square, Eventbrite, DICE, Mailchimp, and Meta Ads through secure API connections. Setup takes minutes, not weeks — and your data stays yours.',
  },
  {
    question: 'How much does VenueIntel cost?',
    answer:
      'Plans start at $349/month for Core, $599 for Pro, and $899 for Operator. Enterprise pricing is custom. Annual billing saves 15%. Founding Design Partners lock in the lowest rate permanently.',
  },
  {
    question: 'What kind of venues is VenueIntel built for?',
    answer:
      "Mid-market nightclubs and music venues doing $1M–$5M in annual revenue. We're built by operators who ran venues at that scale for a decade — so the platform fits how you actually work.",
  },
  {
    question: 'How long does setup take?',
    answer:
      'Most venues are fully connected within one week. Design Partners get white-glove onboarding where we configure every integration, import your data, and train your team personally.',
  },
];

export const FAQ_JSONLD = {
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

/** Generate BreadcrumbList schema from a canonical URL */
export function buildBreadcrumbs(canonical: string): Record<string, unknown> {
  const base = 'https://venueintel.xyz';
  const url = new URL(canonical, base);
  const segments = url.pathname.split('/').filter(Boolean);

  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://venueintel.xyz/' },
  ];

  let path = '';
  segments.forEach((seg, i) => {
    path += `/${seg}`;
    const name = seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    items.push({
      '@type': 'ListItem',
      position: i + 2,
      name: name,
      item: `https://venueintel.xyz${path}`,
    });
  });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}
